import React, { useState, useEffect } from 'react';
import { X, Filter, Download, RefreshCw, CheckCircle, XCircle, Clock, Eye, ChevronDown } from 'lucide-react';
import useVTUStore from '../../../store/vtuStore';
import TransactionDetailsModal from './TransactionDetailsModal';
import { toast } from 'react-hot-toast';

const TransactionHistory = ({ serviceType, onClose }) => {
  const [transactions, setTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    days: 7,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  const { getTransactions, retryTransaction } = useVTUStore();

  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const loadTransactions = async () => {
    setIsLoading(true);
    try {
      const params = {
        serviceType,
        ...(filters.status !== 'all' && { status: filters.status }),
        startDate: new Date(Date.now() - filters.days * 24 * 60 * 60 * 1000).toISOString(),
      };
      
      const response = await getTransactions(params);
      setTransactions(response.transactions || []);
    } catch (error) {
      console.error('Failed to load transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRetry = async (transactionId) => {
    try {
      await retryTransaction(transactionId);
      toast.success('Transaction retry initiated');
      loadTransactions();
    } catch (error) {
      toast.error(error.message || 'Failed to retry transaction');
    }
  };

  const handleExport = () => {
    if (transactions.length === 0) {
      toast.error('No transactions to export');
      return;
    }

    const csv = transactions.map(t => ({
      Date: formatDate(t.createdAt),
      Recipient: t.recipient,
      Amount: t.amount,
      Status: t.status,
      Reference: t.reference || t.vtuReference,
    }));

    const csvContent = "data:text/csv;charset=utf-8," 
      + Object.keys(csv[0]).join(",") + "\n"
      + csv.map(row => Object.values(row).join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `transactions_${serviceType}_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Export successful!');
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const transactionDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - transactionDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return `Today, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffDays === 1) {
      return `Yesterday, ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS':
        return <CheckCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-green-600" />;
      case 'PENDING':
        return <Clock className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-yellow-600" />;
      case 'FAILED':
        return <XCircle className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'bg-green-50 text-green-700 border-green-200';
      case 'PENDING':
        return 'bg-yellow-50 text-yellow-700 border-yellow-200';
      case 'FAILED':
        return 'bg-red-50 text-red-700 border-red-200';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-end sm:items-center justify-center min-h-screen px-3 sm:px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose}></div>

          <div className="inline-block align-bottom bg-white rounded-xl px-3 sm:px-4 pt-4 sm:pt-5 pb-4 text-left overflow-hidden shadow-neomorph transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6 border border-primary-100/30 w-full max-w-[95%] sm:max-w-none">
            <div className="absolute top-0 right-0 pt-2 sm:pt-4 pr-2 sm:pr-4">
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600 bg-[#E8ECF5] rounded-lg p-1.5 shadow-neomorph-inset"
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>
            </div>

            <div className="sm:flex sm:items-start">
              <div className="mt-2 sm:mt-0 text-center sm:text-left w-full">
                <div className="flex items-center justify-center sm:justify-start mb-1">
                  <div className="w-1 h-4 sm:h-5 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full mr-1.5 sm:mr-2" />
                  <h3 className="text-base sm:text-lg font-semibold text-gray-800">
                    {serviceType} Transaction History
                  </h3>
                </div>
              </div>
            </div>

            <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-5">
              {/* Filters - Mobile optimized with dropdown */}
              <div className="bg-[#E8ECF5] rounded-xl p-3 sm:p-4 shadow-neomorph-inset">
                <div className="flex flex-col gap-3">
                  {/* Filter header with mobile button */}
                  <div className="flex items-center justify-between sm:justify-start">
                    <div className="flex items-center space-x-2">
                      <Filter className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-400" />
                      <span className="text-[10px] sm:text-xs font-medium text-gray-700">Filter by:</span>
                    </div>
                    <button
                      onClick={() => setShowFilters(!showFilters)}
                      className="sm:hidden flex items-center space-x-1 px-2.5 py-1.5 bg-white rounded-lg shadow-neomorph-inset"
                    >
                      <span className="text-[11px] font-medium text-gray-700">
                        {filters.status !== 'all' ? filters.status : `${filters.days} days`}
                      </span>
                      <ChevronDown className={`h-3 w-3 text-gray-500 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                    </button>
                  </div>

                  {/* Desktop filters */}
                  <div className="hidden sm:flex flex-wrap gap-2">
                    <select
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                      className="px-3 py-1.5 bg-white rounded-lg border-0 shadow-neomorph text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    >
                      <option value="all">All Status</option>
                      <option value="SUCCESS">Successful</option>
                      <option value="PENDING">Pending</option>
                      <option value="FAILED">Failed</option>
                    </select>
                    <select
                      value={filters.days}
                      onChange={(e) => setFilters({ ...filters, days: parseInt(e.target.value) })}
                      className="px-3 py-1.5 bg-white rounded-lg border-0 shadow-neomorph text-xs font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500/50"
                    >
                      <option value={1}>Last 24 hours</option>
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                    </select>
                    <button
                      onClick={loadTransactions}
                      disabled={isLoading}
                      className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isLoading ? 'animate-spin' : ''}`} />
                      Refresh
                    </button>
                    <button
                      onClick={handleExport}
                      className="bg-white rounded-lg px-3 py-1.5 shadow-neomorph hover:shadow-neomorph-hover transition-all text-xs font-medium text-gray-700 flex items-center border border-gray-100"
                    >
                      <Download className="h-3.5 w-3.5 mr-1.5" />
                      Export
                    </button>
                  </div>

                  {/* Mobile filter options dropdown */}
                  {showFilters && (
                    <div className="sm:hidden space-y-2 pt-2">
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setFilters({ ...filters, status: 'all' });
                            setShowFilters(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${filters.status === 'all' ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-neomorph-inset' : 'bg-white text-gray-700 border border-gray-200 shadow-neomorph'}`}
                        >
                          All Status
                        </button>
                        <button
                          onClick={() => {
                            setFilters({ ...filters, status: 'SUCCESS' });
                            setShowFilters(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${filters.status === 'SUCCESS' ? 'bg-green-50 text-green-700 border border-green-200 shadow-neomorph-inset' : 'bg-white text-gray-700 border border-gray-200 shadow-neomorph'}`}
                        >
                          Successful
                        </button>
                        <button
                          onClick={() => {
                            setFilters({ ...filters, status: 'PENDING' });
                            setShowFilters(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${filters.status === 'PENDING' ? 'bg-yellow-50 text-yellow-700 border border-yellow-200 shadow-neomorph-inset' : 'bg-white text-gray-700 border border-gray-200 shadow-neomorph'}`}
                        >
                          Pending
                        </button>
                        <button
                          onClick={() => {
                            setFilters({ ...filters, status: 'FAILED' });
                            setShowFilters(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${filters.status === 'FAILED' ? 'bg-red-50 text-red-700 border border-red-200 shadow-neomorph-inset' : 'bg-white text-gray-700 border border-gray-200 shadow-neomorph'}`}
                        >
                          Failed
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <button
                          onClick={() => {
                            setFilters({ ...filters, days: 1 });
                            setShowFilters(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${filters.days === 1 ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-neomorph-inset' : 'bg-white text-gray-700 border border-gray-200 shadow-neomorph'}`}
                        >
                          Last 24h
                        </button>
                        <button
                          onClick={() => {
                            setFilters({ ...filters, days: 7 });
                            setShowFilters(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${filters.days === 7 ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-neomorph-inset' : 'bg-white text-gray-700 border border-gray-200 shadow-neomorph'}`}
                        >
                          Last 7 days
                        </button>
                        <button
                          onClick={() => {
                            setFilters({ ...filters, days: 30 });
                            setShowFilters(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${filters.days === 30 ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-neomorph-inset' : 'bg-white text-gray-700 border border-gray-200 shadow-neomorph'}`}
                        >
                          Last 30 days
                        </button>
                        <button
                          onClick={() => {
                            setFilters({ ...filters, days: 90 });
                            setShowFilters(false);
                          }}
                          className={`px-3 py-2 rounded-lg text-xs font-medium text-left ${filters.days === 90 ? 'bg-primary-50 text-primary-700 border border-primary-200 shadow-neomorph-inset' : 'bg-white text-gray-700 border border-gray-200 shadow-neomorph'}`}
                        >
                          Last 90 days
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 pt-1">
                        <button
                          onClick={() => {
                            loadTransactions();
                            setShowFilters(false);
                          }}
                          className="px-3 py-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg text-xs font-medium flex items-center justify-center"
                        >
                          <RefreshCw className="h-3 w-3 mr-1.5" />
                          Apply
                        </button>
                        <button
                          onClick={handleExport}
                          className="px-3 py-2 bg-white text-gray-700 border border-gray-200 rounded-lg text-xs font-medium flex items-center justify-center shadow-neomorph"
                        >
                          <Download className="h-3 w-3 mr-1.5" />
                          Export
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Transactions - Mobile card view, desktop table view */}
              <div className="bg-white rounded-xl overflow-hidden border border-gray-100">
                {/* Mobile: Card View */}
                <div className="sm:hidden divide-y divide-gray-100">
                  {isLoading ? (
                    <div className="text-center py-8">
                      <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-3" />
                      <p className="text-xs text-gray-500">Loading transactions...</p>
                    </div>
                  ) : transactions.length > 0 ? (
                    transactions.map((transaction) => (
                      <div key={transaction.id} className="p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <div>
                              <p className="text-xs font-medium text-gray-800 truncate">
                                {transaction.recipient}
                              </p>
                              <p className="text-[10px] text-gray-500">
                                {formatDate(transaction.createdAt)}
                              </p>
                              {transaction.productDetails?.network && (
                                <p className="text-[9px] text-gray-400 mt-0.5">
                                  {transaction.productDetails.network}
                                </p>
                              )}
                            </div>
                          </div>
                          <div className="text-right flex-shrink-0 ml-2">
                            <p className="text-sm font-semibold text-gray-800">
                              {formatAmount(transaction.amount)}
                            </p>
                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-medium border ${getStatusColor(transaction.status)}`}>
                              {getStatusIcon(transaction.status)}
                              <span className="ml-0.5">{transaction.status}</span>
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center justify-between pt-1">
                          <p className="text-[9px] font-mono text-gray-400 bg-[#E8ECF5] rounded px-1.5 py-0.5">
                            {(transaction.reference || transaction.vtuReference)?.slice(0, 8)}...
                          </p>
                          <div className="flex space-x-1.5">
                            <button
                              onClick={() => setSelectedTransaction(transaction)}
                              className="text-[10px] text-primary-600 bg-primary-50 rounded px-2 py-0.5 shadow-neomorph-inset flex items-center"
                            >
                              <Eye className="h-2.5 w-2.5 mr-0.5" />
                              Details
                            </button>
                            {transaction.status === 'FAILED' && (
                              <button
                                onClick={() => handleRetry(transaction.id)}
                                className="text-[10px] text-primary-600 bg-primary-50 rounded px-2 py-0.5 shadow-neomorph-inset"
                              >
                                Retry
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mx-auto mb-2">
                        <Filter className="h-5 w-5 text-primary-600" />
                      </div>
                      <p className="text-xs text-gray-500 mb-0.5">No transactions found</p>
                      <p className="text-[10px] text-gray-400">Try changing your filters</p>
                    </div>
                  )}
                </div>

                {/* Desktop: Table View */}
                <div className="hidden sm:block overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-[#E8ECF5]">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Recipient
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Reference
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {isLoading ? (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <RefreshCw className="h-8 w-8 animate-spin text-primary-600 mb-3" />
                              <p className="text-sm text-gray-500">Loading transactions...</p>
                            </div>
                          </td>
                        </tr>
                      ) : transactions.length > 0 ? (
                        transactions.map((transaction) => (
                          <tr key={transaction.id} className="hover:bg-[#E8ECF5] transition-colors">
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs text-gray-700">
                                {formatDate(transaction.createdAt)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-800">
                                {transaction.recipient}
                              </div>
                              {transaction.productDetails?.network && (
                                <div className="text-xs text-gray-500">
                                  {transaction.productDetails.network}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-sm font-semibold text-gray-800">
                                {formatAmount(transaction.amount)}
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium border ${getStatusColor(transaction.status)}`}>
                                {getStatusIcon(transaction.status)}
                                <span className="ml-1">{transaction.status}</span>
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="text-xs font-mono text-gray-500 bg-[#E8ECF5] rounded-lg px-2 py-1 shadow-neomorph-inset">
                                {(transaction.reference || transaction.vtuReference)?.slice(0, 8)}...
                              </div>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <div className="flex space-x-2">
                                <button
                                  onClick={() => setSelectedTransaction(transaction)}
                                  className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-2 py-1 shadow-neomorph-inset flex items-center"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Details
                                </button>
                                {transaction.status === 'FAILED' && (
                                  <button
                                    onClick={() => handleRetry(transaction.id)}
                                    className="text-xs text-primary-600 hover:text-primary-700 bg-primary-50 rounded-lg px-2 py-1 shadow-neomorph-inset"
                                  >
                                    Retry
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" className="px-4 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <div className="w-12 h-12 bg-gradient-to-br from-primary-100 to-primary-200 rounded-xl flex items-center justify-center mb-3">
                                <Filter className="h-6 w-6 text-primary-600" />
                              </div>
                              <p className="text-sm text-gray-500 mb-1">No transactions found</p>
                              <p className="text-xs text-gray-400">Try changing your filters</p>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Transaction Details Modal */}
      {selectedTransaction && (
        <TransactionDetailsModal
          transaction={selectedTransaction}
          onClose={() => setSelectedTransaction(null)}
        />
      )}

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
      `}</style>
    </>
  );
};

export default TransactionHistory;